import React, { useState, useEffect } from "react";
import { FiX, FiArrowLeft, FiPlus, FiTrash2, FiSettings } from "react-icons/fi";
import {
  MdFoundation,
  MdManageAccounts,
  MdOutlinePersonRemoveAlt1,
} from "react-icons/md";
import { TfiWorld } from "react-icons/tfi";
import AddedProjectsTab from "./AddedProjectsTab";
import ComingSoonCard from "components/ComingSoon";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import { FaCommentAlt, FaEye, FaRegCreditCard } from "react-icons/fa";
import { HiMiniPlusCircle } from "react-icons/hi2";
import { BsClipboard2CheckFill } from "react-icons/bs";
import { PiSuitcaseSimpleBold } from "react-icons/pi";

const dateTimeFormat = (dateString) => {
  // to dd/mm/yyyy
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const Toggle = ({ enabled, onClick, disabled }) => (
  // <div
  //   className={`${
  //     enabled ? "bg-purple-900" : "bg-gray-400"
  //   } w-12 h-6 rounded-full relative cursor-pointer`}
  //   onClick={onClick}
  // >

  <div
    className={`${
      enabled ? "bg-purple-900" : "bg-gray-400"
    } w-12 h-6 rounded-full relative ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
    onClick={disabled ? undefined : onClick}
  >
    <div
      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
        enabled ? "right-1" : "left-1"
      }`}
    />
  </div>
);

const Checkbox = ({ label, checked, onChange, icon: Icon }) => (
  <div className="flex justify-between items-center px-2">
    <div className="flex items-center gap-2 text-sm">
      {Icon && <Icon className="text-purple-800 w-4 h-4" />}
      <span>{label}</span>
    </div>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="form-checkbox h-4 w-4 text-purple-700 accent-purple-800"
    />
  </div>
);

export default function OrganisationAccessScreen({
  member,
  onClose,
  currentOrgMember,
}) {
  const { addAlert } = useAlert();
  const [activeTab, setActiveTab] = useState("access");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [memberAddress, setMemberAddress] = useState("");
  const [isLoadingAddress] = useState(false);
  const [accessStates, setAccessStates] = useState({
    memberControl: member?.member_control || false,
    procurementControl: member?.procurement_control || false,
    financeControl: member?.finance_control || false,
    vendorControl: member?.vendor_control || false,
    contractorControl: member?.contractor_control || false,
    projectControl: member?.project_control || false,
    designManagement: member?.design_management || false,
    designAdmin: member?.design_admin || false,
    leadControl: member?.lead_control || false, // view + edit
    leadApprover: member?.lead_approver || false, // approve + lock (implies view + edit)
    snagControl: member?.snag_admin || false,
  });
  // const currentUser = JSON.parse(localStorage.getItem("user")) || {};
  const currentUser = JSON.parse(localStorage.getItem("user")) || {};
  const isAdmin = member?.is_admin === true;
  // Prefer address from members API response on the provided member prop
  useEffect(() => {
    const addresses = member?.user?.addresses;
    if (Array.isArray(addresses) && addresses.length > 0) {
      const firstAddress = addresses[0] || {};
      const addressParts = [];
      if (firstAddress.city) addressParts.push(firstAddress.city);
      if (firstAddress.state) addressParts.push(firstAddress.state);
      setMemberAddress(
        addressParts.length > 0
          ? addressParts.join(", ")
          : "Address not available",
      );
    } else {
      setMemberAddress("Address not available");
    }
  }, [member?.user?.addresses]);

  const handleDeleteClick = () => {
    setShowConfirmation(true);
  };

  // Cancel delete operation
  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  // Confirm and execute member deletion
  const confirmDelete = async () => {
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        setShowConfirmation(false);
        return;
      }

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${member.id}/delete/`,
        {
          method: "DELETE",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
        },
      );

      if (response.ok) {
        addAlert("Member removed successfully", "success");
        onClose(); // Close the modal or perform any other action
      } else {
        const errorData = await response.json();
        addAlert(errorData?.message || "Failed to remove member", "error");
      }
    } catch (error) {
      addAlert("Error removing member", "error");
      console.error("Error removing member:", error);
    } finally {
      setShowConfirmation(false);
    }
  };

  const handleSaveChanges = async () => {
    // Logic to save access state changes
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        return;
      }

      console.log("Access States to be saved:", accessStates);

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${member.id}/update/`,
        {
          method: "PATCH",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify({
            member_control: accessStates.memberControl,
            procurement_control: accessStates.procurementControl,
            project_control: accessStates.projectControl,
            finance_control: accessStates.financeControl,
            vendor_control: accessStates.vendorControl,
            contractor_control: accessStates.contractorControl,
            design_management: accessStates.designManagement,
            design_admin: accessStates.designAdmin,
            lead_control: accessStates.leadControl,
            lead_approver: accessStates.leadApprover,
            snag_admin: accessStates.snagControl,
          }),
        },
      );

      if (response.ok) {
        addAlert("Access permissions updated successfully", "success");
      } else {
        const errorData = await response.json();
        addAlert(
          errorData?.message || "Failed to update access permissions",
          "error",
        );
      }
    } catch (error) {
      addAlert("Error updating access permissions", "error");
      console.error("Error updating access permissions:", error);
    }
  };

  // const handleChangeAdminStatus = async (action) => {
  //   try {
  //     const accessToken = localStorage.getItem("access");
  //     if (!accessToken) {
  //       addAlert("Access token not found", "error");
  //       return;
  //     }

  //     const response = await fetch(
  //       `${BASE_URL}/org/organization/member/${member.id}/update/`,
  //       {
  //         method: "PATCH",
  //         headers: new Headers({
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${accessToken}`,
  //         }),
  //         body: JSON.stringify({
  //           is_admin: action === "make" ? true : false,
  //         }),
  //       },
  //     );
  const handleChangeAdminStatus = async (action) => {
    try {
      const accessToken = localStorage.getItem("access");
      if (!accessToken) {
        addAlert("Access token not found", "error");
        return;
      }

      const isGranting = action === "make";

      const body = isGranting
        ? {
            is_admin: true,
            member_control: true,
            procurement_control: true,
            project_control: true,
            finance_control: true,
            vendor_control: true,
            contractor_control: true,
            design_management: true,
            design_admin: true,
            lead_control: true,
            lead_approver: true,
          }
        : { is_admin: false };

      const response = await fetch(
        `${BASE_URL}/org/organization/member/${member.id}/update/`,
        {
          method: "PATCH",
          headers: new Headers({
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          }),
          body: JSON.stringify(body),
        },
      );

      if (response.ok) {
        addAlert(
          `Admin rights ${
            action === "make" ? "granted" : "revoked"
          } successfully`,
          "success",
        );
        onClose();
        // Optionally, you can refresh the member data here
      } else {
        const errorData = await response.json();
        addAlert(
          errorData?.message || `Failed to ${action} admin rights`,
          "error",
        );
      }
    } catch (error) {
      addAlert(`Error trying to ${action} admin rights`, "error");
      console.error(`Error trying to ${action} admin rights:`, error);
    }
  };

  return (
    <div
      className="relative space-y-6 px-6 py-3"
      style={{
        scrollbarWidth: "none",
      }}
    >
      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">
              Confirm Member Removal
            </h3>
            <p className="mb-6">
              Are you sure you want to remove {member?.user?.first_name}{" "}
              {member?.user?.last_name} from this organization? This action
              cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 rounded-md hover:bg-red-700 text-white"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
      {/* HEADER */}
      <div className="bg-white p-6 rounded-xl">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-black"
            >
              <FiArrowLeft className="w-5 h-5" />
            </button>

            {/* Fixed circular profile container */}
            <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-2xl overflow-hidden flex-shrink-0">
              {member?.user?.userpic ? (
                <img
                  src={member.user.userpic}
                  alt={member.user.first_name + " " + member.user.last_name}
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                <span className="select-none">
                  {`${member?.user?.first_name?.[0] || ""}${
                    member?.user?.last_name?.[0] || ""
                  }`}
                </span>
              )}
            </div>

            <div className="w-full">
              <div>
                <h3 className="text-lg font-semibold flex flex-row items-center">
                  {member?.user?.first_name} {member?.user?.last_name}
                  <span className="ml-2 px-2 py-0.5 text-xs bg-purple-100 text-btn-bg rounded-full">
                    {member?.user?.profession || "Member"}
                  </span>
                </h3>
                <p
                  className="text-sm text-gray-500 break-all"
                  style={{
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "break-word",
                  }}
                  title={member?.user?.email}
                >
                  {member?.user?.email}
                </p>
                <div className="text-sm text-gray-600 mt-1">
                  {member.is_admin && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-full mr-2">
                      Admin
                    </span>
                  )}
                  {member.joined_as === "owner" && (
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                      Owner
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {(currentOrgMember.is_admin && !member.is_admin) ||
            currentOrgMember?.joined_as === "owner" ? (
              <button
                onClick={() =>
                  handleChangeAdminStatus(member.is_admin ? "revoke" : "make")
                }
                className="text-gray-600 border-[1px] border-gray-600 px-4 py-2 rounded-full hover:bg-gray-100 font-semibold text-sm flex items-center gap-2"
              >
                <MdManageAccounts className="w-4 h-4" />
                {member.is_admin ? "Revoke Admin" : "Make Admin"}
              </button>
            ) : null}
            <button
              onClick={handleDeleteClick}
              style={{
                display:
                  (currentUser.user === member.user.id ||
                    member.is_admin ||
                    member.joined_as === "owner") &&
                  currentOrgMember?.joined_as !== "owner"
                    ? "none"
                    : "flex",
              }}
              className="text-[#DD2C2C] border-[1px] border-[#DD2C2C] px-4 py-2 rounded-full hover:bg-red-50 font-semibold text-sm flex items-center gap-2"
            >
              <MdOutlinePersonRemoveAlt1 className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
        <hr className="border-t border-gray-700 my-7" />

        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="rounded-lg border p-3">
            <p className="text-gray-500 text-sm">Role</p>
            <p className="text-black font-medium text-base">
              {member?.user?.profession || "Admin"}
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-500 text-sm">Contact details</p>
            <p
              className="text-black font-medium text-base break-all"
              style={{ wordBreak: "break-word", whiteSpace: "normal" }}
              title={member?.user?.email}
            >
              {member?.user?.email}
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-500 text-sm">Address</p>
            <p className="text-black font-medium text-base">
              {isLoadingAddress ? "Loading..." : memberAddress}
            </p>
          </div>
          <div className="rounded-lg border p-3">
            <p className="text-gray-500 text-sm">Joined on</p>
            <p className="text-black font-medium text-base">
              {dateTimeFormat(member?.created_on) || "NA"}
            </p>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="bg-white rounded-xl p-6 space-y-4">
        {/* Tabs */}
        <div className="pb-0 flex flex-row items-center justify-center text-sm w-full">
          <div className="flex justify-center items-center w-1/2">
            <button
              className={`flex items-center gap-1 w-[80%] justify-center py-2 ${
                activeTab === "access"
                  ? "font-medium border-b-[2.5px] border-black text-black"
                  : "text-gray-400"
              }`}
              onClick={() => setActiveTab("access")}
            >
              <TfiWorld className="text-lg" /> Organisation Access
            </button>
          </div>
          <div className="flex justify-center items-center w-1/2">
            <button
              className={`flex items-center w-[80%] justify-center gap-1 py-2 ${
                activeTab === "projects"
                  ? "font-medium border-b-[2.5px] border-black text-black"
                  : "text-gray-400"
              }`}
              onClick={() => setActiveTab("projects")}
            >
              <MdFoundation className="text-xl" /> Added Projects
            </button>
          </div>
        </div>

        {/* ACCESS TAB */}
        {/* {activeTab === "access" && (
          // <ComingSoonCard
          //   stage={6}
          //   featureName="Organisation Access"
          //   description="Manage user access and permissions within your organization."
          // />

        )} */}
        {/* ACCESS TAB */}
        {activeTab === "access" && (
          <div className="space-y-6">
            {/* Permission Cards */}
            <div className="space-y-4">
              {isAdmin && (
                <div className="flex items-center gap-2 px-4 py-2.5 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                  <MdManageAccounts className="w-5 h-5 flex-shrink-0" />
                  <span>
                    This member has <strong>Admin</strong> access — all
                    permissions are granted and locked.
                  </span>
                </div>
              )}
              {/* Edit Organization Members Permission */}
              {/* <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Edit Organization Members
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Grant permission to add, remove, and manage members within
                      the organization
                    </p>
                  </div>
                  <Toggle
                    enabled={accessStates.memberControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        memberControl: !s.memberControl,
                      }))
                    }
                  />
                </div>
              </div> */}

              {/* Add and Manage Projects */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Add and Manage Projects
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Allow the user to create, edit, and oversee projects
                      within the organization
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.projectControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        projectControl: !s.projectControl,
                      }))
                    }
                  /> */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.projectControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        projectControl: !s.projectControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>

              {/* Manage Procurement */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Procurement
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Full access to procurement operations including purchase
                      orders, inventory management, and supplier coordination
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.procurementControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        procurementControl: !s.procurementControl,
                      }))
                    }
                  /> */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.procurementControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        procurementControl: !s.procurementControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>

              {/* Manage Organisation Finance */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Organisation Finance
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Access and manage all financial data, reports, and
                      transactions across the organization
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.financeControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        financeControl: !s.financeControl,
                      }))
                    }
                  /> */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.financeControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        financeControl: !s.financeControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>

              {/* Manage Vendors */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Vendors
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Complete control over vendor relationships, contracts,
                      payments, and performance tracking
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.vendorControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        vendorControl: !s.vendorControl,
                      }))
                    }
                      
                  /> */}
                  {/* Manage Vendors */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.vendorControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        vendorControl: !s.vendorControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>

              {/* Manage Contractors */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Contractors
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Oversee contractor assignments, work orders, payments, and
                      project coordination
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.contractorControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        contractorControl: !s.contractorControl,
                      }))
                    }
                  /> */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.contractorControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        contractorControl: !s.contractorControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>

              {/* Manage Design */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Design
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Control access to the Design module and admin actions like
                      approve/lock
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Design Management
                      </p>
                      <p className="text-xs text-gray-600">
                        Gives access to the Design module/feature
                      </p>
                    </div>
                    <Toggle
                      enabled={isAdmin ? true : accessStates.designManagement}
                      onClick={() =>
                        setAccessStates((s) => ({
                          ...s,
                          designManagement: !s.designManagement,
                        }))
                      }
                      disabled={isAdmin}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Design Admin
                      </p>
                      <p className="text-xs text-gray-600">
                        Can approve and lock designs
                      </p>
                    </div>
                    <Toggle
                      enabled={isAdmin ? true : accessStates.designAdmin}
                      onClick={() =>
                        setAccessStates((s) => ({
                          ...s,
                          designAdmin: !s.designAdmin,
                        }))
                      }
                      disabled={isAdmin}
                    />
                  </div>
                </div>
              </div>

              {/* Lead Management */}
              {/* <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Lead Management
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Control access to Lead Management (view/edit) and approver
                      actions (approve/lock)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Lead Control
                      </p>
                      <p className="text-xs text-gray-600">
                        View and edit access to leads
                      </p>
                    </div>
                    <Toggle
                      enabled={isAdmin ? true : accessStates.leadControl}
                      onClick={() =>
                        setAccessStates((s) => {
                          const nextLeadControl = !s.leadControl;
                          return {
                            ...s,
                            leadControl: nextLeadControl,
                            leadApprover: nextLeadControl
                              ? s.leadApprover
                              : false,
                          };
                        })
                      }
                      disabled={isAdmin}
                    />
                  </div>

                  <div className="flex justify-between items-center">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        Lead Approver
                      </p>
                      <p className="text-xs text-gray-600">
                        Approve and lock access (includes view and edit)
                      </p>
                    </div>
                    <Toggle
                      enabled={isAdmin ? true : accessStates.leadApprover}
                      onClick={() =>
                        setAccessStates((s) => {
                          const nextLeadApprover = !s.leadApprover;
                          return {
                            ...s,
                            leadApprover: nextLeadApprover,
                            leadControl: nextLeadApprover
                              ? true
                              : s.leadControl,
                          };
                        })
                      }
                      disabled={isAdmin}
                    />
                  </div>
                </div>
              </div> */}

              {/* Manage Snags */}
              <div className="border border-gray-300 rounded-xl py-3 px-4 space-y-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-semibold text-base text-gray-900">
                      Manage Snags
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Create, assign, and track snags across projects
                    </p>
                  </div>
                  {/* <Toggle
                    enabled={accessStates.contractorControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        contractorControl: !s.contractorControl,
                      }))
                    }
                  /> */}
                  <Toggle
                    enabled={isAdmin ? true : accessStates.snagControl}
                    onClick={() =>
                      setAccessStates((s) => ({
                        ...s,
                        snagControl: !s.snagControl,
                      }))
                    }
                    disabled={isAdmin}
                  />
                </div>
              </div>
            </div>
            {/* Save button */}
            {/* <div className="flex justify-center pt-4">
              <button
                onClick={handleSaveChanges}
                className="bg-purple-800 text-white px-8 py-2.5 rounded-lg hover:bg-purple-700 transition font-medium"
              >
                Save Changes
              </button>
            </div> */}

            {!isAdmin && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={handleSaveChanges}
                  className="bg-purple-800 text-white px-8 py-2.5 rounded-lg hover:bg-purple-700 transition font-medium"
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        )}

        {/* PROJECT TAB */}
        {activeTab === "projects" && (
          <AddedProjectsTab
            userId={member?.user?.id}
            selectedMember={member}
            currentMember={currentOrgMember}
          />
        )}
      </div>
    </div>
  );
}
